<template>
  <div ref="container" class="d3-chart">
    <div ref="tooltip" class="d3-chart-tooltip" role="tooltip"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';

const DEFAULT_COLORS = [
  '#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16', '#E86452',
  '#6DC8EC', '#9270CA', '#FF9D4D', '#269A99', '#FF99C3'
];

function normalizeValue(point) {
  if (Array.isArray(point)) return Number(point[1]);
  if (point && typeof point === 'object') {
    return Number(point.y !== undefined ? point.y : point.value);
  }
  return Number(point);
}

function getPointName(point, fallback) {
  if (point && typeof point === 'object' && !Array.isArray(point)) {
    return point.name || point.id || fallback;
  }
  return fallback;
}

function getSeriesType(series, options) {
  return series.type || (options.chart && options.chart.type) || 'line';
}

export default {
  name: 'D3Chart',
  props: {
    options: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      resizeObserver: null
    };
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.$nextTick(this.renderChart);
      }
    }
  },
  mounted() {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.renderChart());
      this.resizeObserver.observe(this.$refs.container);
    } else {
      window.addEventListener('resize', this.renderChart);
    }

    this.renderChart();
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.renderChart);
    }
  },
  methods: {
    renderChart() {
      if (!this.$refs.container || !this.options) return;

      const container = this.$refs.container;
      const width = Math.max(container.clientWidth || 0, 320);
      const height = this.getHeight();

      d3.select(container).selectAll('svg').remove();

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('role', 'img');

      this.drawTitle(svg, width);

      const type = this.getChartType();
      if (type === 'pie') {
        this.drawPie(svg, width, height);
      } else if (type === 'packedbubble') {
        this.drawPackedBubble(svg, width, height);
      } else if (type === 'boxplot') {
        this.drawBoxplot(svg, width, height);
      } else if (type === 'scatter') {
        this.drawScatter(svg, width, height);
      } else {
        this.drawCartesian(svg, width, height, type);
      }
    },

    getChartType() {
      const firstSeries = (this.options.series && this.options.series[0]) || {};
      return (this.options.chart && this.options.chart.type) || firstSeries.type || 'column';
    },

    getHeight() {
      const configuredHeight = this.options.chart && this.options.chart.height;
      if (Number(configuredHeight)) return Number(configuredHeight);
      return this.getChartType() === 'packedbubble' ? 430 : 360;
    },

    getCategories() {
      return (this.options.xAxis && this.options.xAxis.categories) || [];
    },

    getPlotArea(width, height, marginOverride) {
      const margin = Object.assign({
        top: this.options.title && this.options.title.text ? 44 : 24,
        right: 24,
        bottom: 76,
        left: 58
      }, marginOverride || {});

      return {
        margin,
        innerWidth: Math.max(width - margin.left - margin.right, 1),
        innerHeight: Math.max(height - margin.top - margin.bottom, 1)
      };
    },

    drawTitle(svg, width) {
      const title = this.options.title && this.options.title.text;
      const subtitle = this.options.subtitle && this.options.subtitle.text;

      if (title) {
        svg.append('text')
          .attr('class', 'd3-chart-title')
          .attr('x', width / 2)
          .attr('y', 22)
          .attr('text-anchor', 'middle')
          .text(title);
      }

      if (subtitle) {
        svg.append('text')
          .attr('class', 'd3-chart-subtitle')
          .attr('x', width / 2)
          .attr('y', title ? 42 : 22)
          .attr('text-anchor', 'middle')
          .html(subtitle.replace(/&nbsp;/g, ' '));
      }
    },

    showTooltip(event, lines) {
      const tooltip = this.$refs.tooltip;
      const container = this.$refs.container;
      if (!tooltip || !container) return;

      tooltip.innerHTML = lines.filter(Boolean).join('<br>');
      tooltip.style.opacity = '1';
      tooltip.style.visibility = 'hidden';
      tooltip.style.left = '0px';
      tooltip.style.top = '0px';

      const containerRect = container.getBoundingClientRect();
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      const cursorX = event.clientX - containerRect.left;
      const cursorY = event.clientY - containerRect.top;
      const gap = 14;

      let left = cursorX + gap;
      let top = cursorY + gap;

      if (left + tooltipWidth + gap > containerRect.width) {
        left = cursorX - tooltipWidth - gap;
      }

      if (top + tooltipHeight + gap > containerRect.height) {
        top = cursorY - tooltipHeight - gap;
      }

      tooltip.style.left = `${Math.max(8, left)}px`;
      tooltip.style.top = `${Math.max(8, top)}px`;
      tooltip.style.visibility = 'visible';
    },

    hideTooltip() {
      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;

      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
    },

    colorFor(index, explicitColor) {
      return explicitColor || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    },

    drawLegend(svg, series, width, height) {
      if (this.options.legend && this.options.legend.enabled === false) return;
      const visibleSeries = series.filter(item => item && item.name);
      if (!visibleSeries.length) return;

      const legend = svg.append('g')
        .attr('class', 'd3-chart-legend')
        .attr('transform', `translate(18, ${height - 24})`);

      let x = 0;
      visibleSeries.forEach((item, index) => {
        const textWidth = String(item.name).length * 7 + 34;
        const row = legend.append('g')
          .attr('transform', `translate(${x}, 0)`);

        row.append('rect')
          .attr('width', 12)
          .attr('height', 12)
          .attr('rx', 2)
          .attr('fill', this.colorFor(index, item.color));

        row.append('text')
          .attr('x', 18)
          .attr('y', 10)
          .text(item.name);

        x += Math.min(textWidth, width - 36);
      });
    },

    drawPie(svg, width, height) {
      const series = (this.options.series && this.options.series[0]) || { data: [] };
      const data = (series.data || []).filter(point => normalizeValue(point) > 0);
      if (!data.length) {
        this.drawEmpty(svg, width, height);
        return;
      }

      const radius = Math.min(width, height - 70) / 2 - 18;
      const innerSize = this.options.plotOptions &&
        this.options.plotOptions.pie &&
        this.options.plotOptions.pie.innerSize;
      const innerRadius = typeof innerSize === 'string' && innerSize.indexOf('%') > -1
        ? radius * parseFloat(innerSize) / 100
        : Number(innerSize) || 0;

      const group = svg.append('g')
        .attr('transform', `translate(${width / 2}, ${(height - 36) / 2})`);

      const pie = d3.pie().value(normalizeValue).sort(null);
      const arc = d3.arc().innerRadius(innerRadius).outerRadius(radius);
      const labelArc = d3.arc().innerRadius(radius * 0.68).outerRadius(radius * 0.68);
      const total = d3.sum(data, normalizeValue);

      group.selectAll('path')
        .data(pie(data))
        .join('path')
        .attr('d', arc)
        .attr('fill', (d, index) => this.colorFor(index, d.data.color))
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .style('cursor', d => d.data.url ? 'pointer' : 'default')
        .on('click', (event, d) => {
          if (d.data.url) window.location.href = d.data.url;
        })
        .on('mousemove', (event, d) => {
          const value = normalizeValue(d.data);
          this.showTooltip(event, [
            `<b>${getPointName(d.data, '')}</b>`,
            `count: ${value}`,
            `${series.name || 'ratio'}: ${((value / total) * 100).toFixed(1)}%`
          ]);
        })
        .on('mouseleave', this.hideTooltip);

      group.selectAll('text')
        .data(pie(data).filter(d => (d.endAngle - d.startAngle) > 0.18))
        .join('text')
        .attr('transform', d => `translate(${labelArc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('class', 'd3-chart-pie-label')
        .text(d => getPointName(d.data, ''));
    },

    drawPackedBubble(svg, width, height) {
      const series = this.options.series || [];
      const leaves = [];
      series.forEach((group, groupIndex) => {
        (group.data || []).forEach(point => {
          const value = normalizeValue(point);
          if (value > 0) {
            leaves.push({
              group: group.name,
              groupIndex,
              name: getPointName(point, ''),
              value,
              color: point.color || group.color
            });
          }
        });
      });

      if (!leaves.length) {
        this.drawEmpty(svg, width, height);
        return;
      }

      const root = d3.hierarchy({ children: leaves })
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value);

      d3.pack()
        .size([width - 24, height - 56])
        .padding(8)(root);

      const group = svg.append('g').attr('transform', 'translate(12, 22)');
      const nodes = group.selectAll('g')
        .data(root.leaves())
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`);

      nodes.append('circle')
        .attr('r', d => d.r)
        .attr('fill', d => this.colorFor(d.data.groupIndex, d.data.color))
        .attr('fill-opacity', 0.78)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .on('mousemove', (event, d) => {
          this.showTooltip(event, [
            `<b>${d.data.name}</b>`,
            `${d.data.value} entries`,
            d.data.group
          ]);
        })
        .on('mouseleave', this.hideTooltip);

      nodes.filter(d => d.r > 20)
        .append('text')
        .attr('class', 'd3-chart-bubble-label')
        .attr('text-anchor', 'middle')
        .selectAll('tspan')
        .data(d => this.wrapLabel(d.data.name, Math.max(1, Math.floor(d.r / 4))).slice(0, 3))
        .join('tspan')
        .attr('x', 0)
        .attr('dy', (d, index) => index === 0 ? '-0.2em' : '1.1em')
        .text(d => d);
    },

    drawCartesian(svg, width, height, chartType) {
      const series = (this.options.series || []).filter(item => item && item.data);
      const categories = this.getCategories();
      const isBar = chartType === 'bar';
      const stacking = this.options.plotOptions &&
        (this.options.plotOptions.series || this.options.plotOptions.column || {}).stacking;

      if (!series.length || !categories.length) {
        this.drawEmpty(svg, width, height);
        return;
      }

      const plot = this.getPlotArea(width, height, { left: isBar ? 128 : 58, bottom: isBar ? 48 : 86 });
      const g = svg.append('g').attr('transform', `translate(${plot.margin.left},${plot.margin.top})`);
      const values = series.flatMap(item => item.data.map(normalizeValue)).filter(Number.isFinite);
      const max = this.getYAxisMax(values, series, stacking, categories.length);

      if (isBar) {
        this.drawHorizontalBars(g, series, categories, plot, max, stacking);
      } else {
        this.drawColumns(g, series, categories, plot, max, stacking);
      }

      const yTitle = this.options.yAxis && this.options.yAxis.title && this.options.yAxis.title.text;
      if (yTitle && !isBar) {
        svg.append('text')
          .attr('class', 'd3-chart-axis-title')
          .attr('transform', 'rotate(-90)')
          .attr('x', -plot.margin.top - plot.innerHeight / 2)
          .attr('y', 16)
          .attr('text-anchor', 'middle')
          .text(yTitle);
      }

      this.drawLegend(svg, series, width, height);
    },

    getYAxisMax(values, series, stacking, categoryCount) {
      const yAxis = this.options.yAxis || {};
      if (Number.isFinite(Number(yAxis.max))) return Number(yAxis.max);
      if (stacking === 'percent') return 100;

      if (stacking === 'normal') {
        const totals = d3.range(categoryCount).map(index =>
          d3.sum(series, item => Math.max(0, normalizeValue(item.data[index])))
        );
        return Math.max(d3.max(totals) || 0, 1);
      }

      return Math.max(d3.max(values) || 0, 1);
    },

    drawColumns(g, series, categories, plot, max, stacking) {
      const x0 = d3.scaleBand().domain(categories).range([0, plot.innerWidth]).padding(0.16);
      const x1 = d3.scaleBand().domain(series.map(item => item.name)).range([0, x0.bandwidth()]).padding(0.08);
      const y = d3.scaleLinear().domain([0, max]).nice().range([plot.innerHeight, 0]);

      g.append('g')
        .attr('class', 'd3-chart-axis')
        .attr('transform', `translate(0,${plot.innerHeight})`)
        .call(d3.axisBottom(x0).tickSizeOuter(0))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .attr('text-anchor', 'end');

      g.append('g')
        .attr('class', 'd3-chart-axis')
        .call(d3.axisLeft(y).ticks(6));

      if (stacking) {
        categories.forEach((category, categoryIndex) => {
          const total = d3.sum(series, item => Math.max(0, normalizeValue(item.data[categoryIndex]))) || 1;
          let yOffset = 0;
          series.forEach((item, seriesIndex) => {
            const rawValue = Math.max(0, normalizeValue(item.data[categoryIndex]));
            const value = stacking === 'percent' ? rawValue / total * 100 : rawValue;
            const y1 = y(yOffset + value);
            const y0 = y(yOffset);
            g.append('rect')
              .attr('x', x0(category))
              .attr('y', y1)
              .attr('width', x0.bandwidth())
              .attr('height', Math.max(y0 - y1, 0))
              .attr('fill', this.colorFor(seriesIndex, item.color))
              .on('mousemove', event => {
                this.showTooltip(event, [
                  `<b>${category}</b>`,
                  `${item.name}: ${rawValue}`,
                  stacking === 'percent' ? `${value.toFixed(2)}%` : null
                ]);
              })
              .on('mouseleave', this.hideTooltip);
            yOffset += value;
          });
        });
        return;
      }

      const group = g.selectAll('.d3-chart-column-group')
        .data(categories)
        .join('g')
        .attr('transform', category => `translate(${x0(category)},0)`);

      series.forEach((item, seriesIndex) => {
        group.append('rect')
          .attr('x', x1(item.name))
          .attr('y', (category, categoryIndex) => y(Math.max(0, normalizeValue(item.data[categoryIndex]))))
          .attr('width', x1.bandwidth())
          .attr('height', (category, categoryIndex) => plot.innerHeight - y(Math.max(0, normalizeValue(item.data[categoryIndex]))))
          .attr('fill', this.colorFor(seriesIndex, item.color))
          .on('mousemove', (event, category) => {
            const categoryIndex = categories.indexOf(category);
            this.showTooltip(event, [
              `<b>${category}</b>`,
              `${item.name}: ${normalizeValue(item.data[categoryIndex])}`
            ]);
          })
          .on('mouseleave', this.hideTooltip);
      });
    },

    drawHorizontalBars(g, series, categories, plot, max, stacking) {
      const y = d3.scaleBand().domain(categories).range([0, plot.innerHeight]).padding(0.18);
      const x = d3.scaleLinear().domain([0, max]).nice().range([0, plot.innerWidth]);

      g.append('g')
        .attr('class', 'd3-chart-axis')
        .call(d3.axisLeft(y).tickSizeOuter(0));

      g.append('g')
        .attr('class', 'd3-chart-axis')
        .attr('transform', `translate(0,${plot.innerHeight})`)
        .call(d3.axisBottom(x).ticks(5));

      categories.forEach((category, categoryIndex) => {
        let xOffset = 0;
        series.forEach((item, seriesIndex) => {
          const value = Math.max(0, normalizeValue(item.data[categoryIndex]));
          g.append('rect')
            .attr('x', x(xOffset))
            .attr('y', y(category))
            .attr('width', x(value))
            .attr('height', y.bandwidth())
            .attr('fill', this.colorFor(seriesIndex, item.color))
            .on('mousemove', event => {
              this.showTooltip(event, [
                `<b>${category}</b>`,
                `${item.name}: ${value}`
              ]);
            })
            .on('mouseleave', this.hideTooltip);
          xOffset += stacking === 'normal' ? value : 0;
        });
      });
    },

    drawScatter(svg, width, height) {
      const series = (this.options.series || []).filter(item => item && item.data);
      const points = series.flatMap((item, seriesIndex) =>
        item.data.map(point => ({
          seriesName: item.name,
          seriesIndex,
          color: item.color,
          x: Array.isArray(point) ? Number(point[0]) : Number(point.x),
          y: Array.isArray(point) ? Number(point[1]) : Number(point.y)
        }))
      ).filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));

      if (!points.length) {
        this.drawEmpty(svg, width, height);
        return;
      }

      const plot = this.getPlotArea(width, height);
      const g = svg.append('g').attr('transform', `translate(${plot.margin.left},${plot.margin.top})`);
      const x = d3.scaleLinear().domain(d3.extent(points, d => d.x)).nice().range([0, plot.innerWidth]);
      const yDomain = [
        Number.isFinite(Number(this.options.yAxis && this.options.yAxis.min)) ? Number(this.options.yAxis.min) : d3.min(points, d => d.y),
        Number.isFinite(Number(this.options.yAxis && this.options.yAxis.max)) ? Number(this.options.yAxis.max) : d3.max(points, d => d.y)
      ];
      const y = d3.scaleLinear().domain(yDomain).nice().range([plot.innerHeight, 0]);

      g.append('g').attr('class', 'd3-chart-axis').attr('transform', `translate(0,${plot.innerHeight})`).call(d3.axisBottom(x).ticks(6));
      g.append('g').attr('class', 'd3-chart-axis').call(d3.axisLeft(y).ticks(6));

      g.selectAll('circle')
        .data(points)
        .join('circle')
        .attr('cx', d => x(d.x))
        .attr('cy', d => y(d.y))
        .attr('r', 4)
        .attr('fill', d => this.colorFor(d.seriesIndex, d.color))
        .attr('fill-opacity', 0.78)
        .on('mousemove', (event, d) => {
          this.showTooltip(event, [
            `<b>${d.seriesName}</b>`,
            `Exp: ${d.x}`,
            `PSI: ${d.y}`
          ]);
        })
        .on('mouseleave', this.hideTooltip);

      this.drawLegend(svg, series, width, height);
    },

    drawBoxplot(svg, width, height) {
      const series = (this.options.series || []).filter(item => item && item.data);
      const boxSeries = series.find(item => getSeriesType(item, this.options) === 'boxplot') || series[0];
      const scatterSeries = series.filter(item => getSeriesType(item, this.options) === 'scatter');
      const categories = this.getCategories().length
        ? this.getCategories()
        : (boxSeries.data || []).map((item, index) => getPointName(item, String(index + 1)));

      const boxes = (boxSeries.data || []).map((item, index) => {
        const values = Array.isArray(item) ? item.map(Number) : [item.low, item.q1, item.median, item.q3, item.high].map(Number);
        return { category: categories[index] || String(index + 1), values, index };
      }).filter(item => item.values.every(Number.isFinite));

      if (!boxes.length) {
        this.drawEmpty(svg, width, height);
        return;
      }

      const plot = this.getPlotArea(width, height, { bottom: 92 });
      const g = svg.append('g').attr('transform', `translate(${plot.margin.left},${plot.margin.top})`);
      const allValues = boxes.flatMap(item => item.values);
      const y = d3.scaleLinear()
        .domain([
          Number.isFinite(Number(this.options.yAxis && this.options.yAxis.min)) ? Number(this.options.yAxis.min) : d3.min(allValues),
          Number.isFinite(Number(this.options.yAxis && this.options.yAxis.max)) ? Number(this.options.yAxis.max) : d3.max(allValues)
        ])
        .nice()
        .range([plot.innerHeight, 0]);
      const x = d3.scaleBand().domain(categories).range([0, plot.innerWidth]).padding(0.28);
      const boxWidth = Math.max(10, x.bandwidth() * 0.72);
      const boxOptions = (this.options.plotOptions && this.options.plotOptions.boxplot) || {};

      g.append('g')
        .attr('class', 'd3-chart-axis')
        .attr('transform', `translate(0,${plot.innerHeight})`)
        .call(d3.axisBottom(x).tickSizeOuter(0))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .attr('text-anchor', 'end');

      g.append('g')
        .attr('class', 'd3-chart-axis')
        .call(d3.axisLeft(y).ticks(6));

      const boxGroup = g.selectAll('.d3-chart-boxplot')
        .data(boxes)
        .join('g')
        .attr('class', 'd3-chart-boxplot')
        .attr('transform', d => `translate(${x(d.category) + x.bandwidth() / 2},0)`);

      boxGroup.append('line')
        .attr('y1', d => y(d.values[0]))
        .attr('y2', d => y(d.values[4]))
        .attr('stroke', boxOptions.stemColor || '#444')
        .attr('stroke-width', boxOptions.stemWidth || 1.5);

      boxGroup.append('rect')
        .attr('x', -boxWidth / 2)
        .attr('y', d => y(d.values[3]))
        .attr('width', boxWidth)
        .attr('height', d => Math.max(y(d.values[1]) - y(d.values[3]), 1))
        .attr('fill', (d, index) => {
          if (boxOptions.colorByPoint && boxOptions.colors) return boxOptions.colors[index % boxOptions.colors.length];
          return boxOptions.fillColor || '#F0F0E0';
        })
        .attr('fill-opacity', boxOptions.fillColor === null ? 0.22 : 0.85)
        .attr('stroke', '#4A4A4A')
        .attr('stroke-width', boxOptions.lineWidth || 1.5)
        .on('mousemove', (event, d) => {
          this.showTooltip(event, [
            `<b>${d.category}</b>`,
            `high: ${d.values[4]}`,
            `q3: ${d.values[3]}`,
            `median: ${d.values[2]}`,
            `q1: ${d.values[1]}`,
            `low: ${d.values[0]}`
          ]);
        })
        .on('mouseleave', this.hideTooltip);

      boxGroup.append('line')
        .attr('x1', -boxWidth / 2)
        .attr('x2', boxWidth / 2)
        .attr('y1', d => y(d.values[2]))
        .attr('y2', d => y(d.values[2]))
        .attr('stroke', boxOptions.medianColor || '#0C5DA5')
        .attr('stroke-width', boxOptions.medianWidth || 2);

      boxGroup.append('line')
        .attr('x1', -boxWidth / 4)
        .attr('x2', boxWidth / 4)
        .attr('y1', d => y(d.values[0]))
        .attr('y2', d => y(d.values[0]))
        .attr('stroke', boxOptions.whiskerColor || '#444')
        .attr('stroke-width', boxOptions.whiskerWidth || 2);

      boxGroup.append('line')
        .attr('x1', -boxWidth / 4)
        .attr('x2', boxWidth / 4)
        .attr('y1', d => y(d.values[4]))
        .attr('y2', d => y(d.values[4]))
        .attr('stroke', boxOptions.whiskerColor || '#444')
        .attr('stroke-width', boxOptions.whiskerWidth || 2);

      scatterSeries.forEach(item => {
        const points = (item.data || []).map(point => ({
          xIndex: Array.isArray(point) ? Number(point[0]) : Number(point.x),
          y: Array.isArray(point) ? Number(point[1]) : Number(point.y)
        })).filter(point => Number.isFinite(point.xIndex) && Number.isFinite(point.y));

        const scatterGroup = g.append('g').attr('class', 'd3-chart-box-scatter');

        scatterGroup.selectAll('circle')
          .data(points)
          .join('circle')
          .attr('cx', d => {
            const category = categories[Math.round(d.xIndex)];
            return (x(category) || 0) + x.bandwidth() / 2 + (Math.random() - 0.5) * x.bandwidth() * 0.45;
          })
          .attr('cy', d => y(d.y))
          .attr('r', item.marker && item.marker.radius ? item.marker.radius : 2)
          .attr('fill', item.color || 'rgba(100, 100, 100, 0.5)')
          .on('mousemove', (event, d) => this.showTooltip(event, [`Value: ${d.y}`]))
          .on('mouseleave', this.hideTooltip);
      });
    },

    wrapLabel(label, maxChars) {
      const words = String(label).split(/\s+/);
      const lines = [];
      let line = '';

      words.forEach(word => {
        const next = line ? `${line} ${word}` : word;
        if (next.length > maxChars && line) {
          lines.push(line);
          line = word;
        } else {
          line = next;
        }
      });

      if (line) lines.push(line);
      return lines;
    },

    drawEmpty(svg, width, height) {
      svg.append('text')
        .attr('class', 'd3-chart-empty')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .text('No chart data');
    }
  }
};
</script>

<style scoped>
.d3-chart {
  position: relative;
  width: 100%;
  min-height: 320px;
  font-family: Arial, "Roboto", sans-serif;
}

.d3-chart >>> svg {
  display: block;
  width: 100%;
  height: auto;
}

.d3-chart >>> .d3-chart-title {
  font-size: 15px;
  font-weight: 700;
  fill: #222;
}

.d3-chart >>> .d3-chart-subtitle {
  font-size: 13px;
  font-weight: 700;
  fill: #333;
}

.d3-chart >>> .d3-chart-axis text,
.d3-chart >>> .d3-chart-legend text {
  font-size: 11px;
  fill: #333;
}

.d3-chart >>> .d3-chart-axis path,
.d3-chart >>> .d3-chart-axis line {
  stroke: #9E9E9E;
}

.d3-chart >>> .d3-chart-axis-title {
  font-size: 12px;
  font-weight: 700;
  fill: #333;
}

.d3-chart >>> .d3-chart-pie-label,
.d3-chart >>> .d3-chart-bubble-label {
  font-size: 11px;
  font-weight: 700;
  fill: #222;
  pointer-events: none;
}

.d3-chart >>> .d3-chart-empty {
  fill: #777;
  font-size: 13px;
}

.d3-chart-tooltip {
  position: absolute;
  z-index: 5;
  max-width: 260px;
  padding: 9px 11px;
  border: 1px solid rgba(25, 118, 210, 0.18);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 8px 22px rgba(22, 42, 70, 0.18);
  color: #263238;
  font-size: 12px;
  line-height: 1.4;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 120ms ease;
  word-break: normal;
  overflow-wrap: break-word;
}

.d3-chart-tooltip >>> b {
  color: #0D47A1;
  font-weight: 700;
}
</style>
